import React, { useState } from 'react'
import { Button } from '~/components/common/Button'
import Input from '~/components/common/Input'
import { RadioGroupV2, RadioOption } from '~/components/common/RadioGroupV2'
import { Typography } from '~/components/common/Typography'
import { parseMessageId } from '~/containers/conversationChatUtils'
import { useCurrentConversation } from '~/contexts/CurrentConversationContext'
import { useWorkspaceContext } from '~/contexts/WorkspaceContext'
import { cm, fetchWrapper } from '~/utils'
import type { ChatMessage, ChatMessageTextFeedback } from './chatTypes'

interface AssistantResponseFeedbackProps {
  message: ChatMessage
  onFeedbackUpdate?: (
    messageId: string,
    feedback: 'positive' | 'negative' | undefined,
    feedbackReason?: string,
    otherReason?: string
  ) => void
  trailingAction?: React.ReactNode
}

export const responseActionButtonClassName =
  'h-7 w-7 shrink-0 rounded-full p-0 shadow-none text-tokens-neutral-icon-default hover:text-tokens-neutral-icon-hover disabled:cursor-not-allowed disabled:opacity-60'

export function AssistantResponseFeedback({
  message,
  onFeedbackUpdate,
  trailingAction
}: AssistantResponseFeedbackProps) {
  const { currentWorkspace } = useWorkspaceContext()
  const { conversation } = useCurrentConversation()
  const [selectedReason, setSelectedReason] = useState<string>('')
  const [otherReason, setOtherReason] = useState<string>('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const feedbackReasons: RadioOption[] = [
    { value: 'Inaccurate answer', content: 'Inaccurate answer' },
    {
      value: 'Output generation took too many iterations',
      content: 'Output generation took too many iterations'
    },
    {
      value: 'Other',
      content: (
        <div className='w-full'>
          <div>Other</div>
          {selectedReason === 'Other' && (
            <Input
              value={otherReason}
              onChange={e => setOtherReason(e.target.value)}
              placeholder='Tell us more (Optional)'
              className='mt-3'
            />
          )}
        </div>
      )
    }
  ]

  const handleFeedback = async (
    type: ChatMessageTextFeedback,
    feedbackReason: string,
    otherReason: string
  ) => {
    // Only support feedback for text messages
    if (message.type !== 'text' || !currentWorkspace || !conversation || isSubmitting) {
      return
    }

    // Parse the message ID to extract database ID (e.g., "inline-19-0" -> "19")
    const dbMessageId = parseMessageId(message.id)
    if (!dbMessageId) {
      console.error('Failed to parse message ID:', message.id)
      return
    }

    setIsSubmitting(true)

    try {
      let newFeedback: 'positive' | 'negative' | undefined = type
      let newFeedbackReason: string | undefined = feedbackReason
      let newOtherReason: string | undefined = otherReason

      // Only delete if clicking the same button again AND no reason is being provided
      // If a reason is provided (from the form), always submit/update the feedback
      if (message.feedback === type && !feedbackReason) {
        // Remove feedback if clicking the same button again without a reason
        await fetchWrapper.delete(
          `/api/workspace/${currentWorkspace.uuid}/conversation/${conversation.uuid}/message/${dbMessageId}/feedback`
        )
        newFeedback = undefined
        newFeedbackReason = undefined
        newOtherReason = undefined
      } else {
        // Submit new feedback or update existing feedback with reason
        await fetchWrapper.post(
          `/api/workspace/${currentWorkspace.uuid}/conversation/${conversation.uuid}/message/${dbMessageId}/feedback`,
          {
            feedback: type,
            feedback_reason: feedbackReason,
            other_reason: otherReason
          }
        )
      }

      // Update UI locally without refetching
      if (onFeedbackUpdate) {
        onFeedbackUpdate(message.id, newFeedback, newFeedbackReason, newOtherReason)
      }

      // Reset form state after successful submission
      setSelectedReason('')
      setOtherReason('')
    } catch (error) {
      console.error('Failed to submit feedback:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Only support feedback for text messages
  if (message.type !== 'text') {
    return null
  }

  return (
    <div className='flex flex-col gap-2 w-full min-w-0'>
      <div className='flex flex-row gap-4 items-center'>
        <Button
          variant='borderless'
          onClick={() => handleFeedback('positive', '', '')}
          isDisabled={isSubmitting}
          className={cm(
            responseActionButtonClassName,
            message.feedback === 'positive' && 'text-tokens-neutral-icon-hover'
          )}
          aria-label='Mark response as helpful'
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='text-tokens-neutral-icon-default'
          >
            <g clipPath='url(#clip0_129_9811)'>
              <path
                d='M4.66667 7.33332V12.6667C4.66667 12.8435 4.59643 13.013 4.4714 13.1381C4.34638 13.2631 4.17681 13.3333 4 13.3333H2.66667C2.48986 13.3333 2.32029 13.2631 2.19526 13.1381C2.07024 13.013 2 12.8435 2 12.6667V7.99999C2 7.82318 2.07024 7.65361 2.19526 7.52859C2.32029 7.40356 2.48986 7.33332 2.66667 7.33332H4.66667ZM4.66667 7.33332C5.37391 7.33332 6.05219 7.05237 6.55228 6.55227C7.05238 6.05218 7.33333 5.3739 7.33333 4.66666V3.99999C7.33333 3.64637 7.47381 3.30723 7.72386 3.05718C7.97391 2.80713 8.31304 2.66666 8.66667 2.66666C9.02029 2.66666 9.35943 2.80713 9.60948 3.05718C9.85952 3.30723 10 3.64637 10 3.99999V7.33332H12C12.3536 7.33332 12.6928 7.4738 12.9428 7.72385C13.1929 7.9739 13.3333 8.31303 13.3333 8.66666L12.6667 12C12.5708 12.409 12.3889 12.7602 12.1484 13.0006C11.908 13.2411 11.6219 13.3579 11.3333 13.3333H6.66667C6.13623 13.3333 5.62753 13.1226 5.25245 12.7475C4.87738 12.3725 4.66667 11.8638 4.66667 11.3333'
                fill={message.feedback === 'positive' ? 'currentColor' : 'none'}
                stroke='currentColor'
                strokeWidth={message.feedback === 'positive' ? '0' : '1'}
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </g>
            <defs>
              <clipPath id='clip0_129_9811'>
                <rect width='16' height='16' fill='currentColor' />
              </clipPath>
            </defs>
          </svg>
        </Button>
        <Button
          variant='borderless'
          onClick={() => handleFeedback('negative', '', '')}
          isDisabled={isSubmitting}
          className={cm(
            responseActionButtonClassName,
            message.feedback === 'negative' && 'text-tokens-neutral-icon-hover'
          )}
          aria-label='Mark response as not helpful'
        >
          <svg
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
            className='text-tokens-neutral-icon-default'
          >
            <g clipPath='url(#clip0_129_9311)'>
              <path
                d='M4.66667 8.66666V3.33333C4.66667 3.15652 4.59643 2.98695 4.4714 2.86192C4.34638 2.7369 4.17681 2.66666 4 2.66666H2.66667C2.48986 2.66666 2.32029 2.7369 2.19526 2.86192C2.07024 2.98695 2 3.15652 2 3.33333V8C2 8.17681 2.07024 8.34638 2.19526 8.4714C2.32029 8.59642 2.48986 8.66666 2.66667 8.66666H4.66667ZM4.66667 8.66666C5.37391 8.66666 6.05219 8.94761 6.55228 9.44771C7.05238 9.94781 7.33333 10.6261 7.33333 11.3333V12C7.33333 12.3536 7.47381 12.6928 7.72386 12.9428C7.97391 13.1929 8.31304 13.3333 8.66667 13.3333C9.02029 13.3333 9.35943 13.1929 9.60948 12.9428C9.85952 12.6928 10 12.3536 10 12V8.66666H12C12.3536 8.66666 12.6928 8.52619 12.9428 8.27614C13.1929 8.02609 13.3333 7.68695 13.3333 7.33333L12.6667 4C12.5708 3.59101 12.3889 3.23983 12.1484 2.99935C11.908 2.75887 11.6219 2.64211 11.3333 2.66666H6.66667C6.13623 2.66666 5.62753 2.87738 5.25245 3.25245C4.87738 3.62752 4.66667 4.13623 4.66667 4.66666'
                fill={message.feedback === 'negative' ? 'currentColor' : 'none'}
                stroke='currentColor'
                strokeWidth={message.feedback === 'negative' ? '0' : '1'}
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </g>
            <defs>
              <clipPath id='clip0_129_9311'>
                <rect width='16' height='16' fill='currentColor' />
              </clipPath>
            </defs>
          </svg>
        </Button>
        {trailingAction}
        {message.feedback === 'negative' && message.feedbackReason && (
          <Typography
            fontFamily='geist'
            fontSize='bodySm'
            fontWeight='bold'
            className='text-tokens-neutral-text-tertiary'
          >
            Feedback received
          </Typography>
        )}
      </div>
      {message.feedback === 'negative' && (
        <>
          {!message.feedbackReason && (
            <div className='mt-2 rounded-3xl border border-tokens-neutral-border-colored bg-tokens-neutral-fill-fill-1 p-6'>
              {/* Title */}
              <Typography
                fontFamily='geist'
                fontSize='titleSm'
                fontWeight='bold'
                className='text-tokens-neutral-text-primary'
              >
                What went wrong?
              </Typography>

              {/* Radio Options */}
              <div className='mx-[-24px] my-3'>
                <RadioGroupV2
                  options={feedbackReasons}
                  selectedValue={selectedReason}
                  onValueChange={setSelectedReason}
                />
              </div>

              {/* Buttons - bottom action bar (aligned with PlanDialog style) */}
              <div className='flex gap-2 justify-end items-center'>
                <Button
                  variant='outlined'
                  onClick={() => handleFeedback('negative', '', '')}
                  className='h-9 border-tokens-neutral-border-1 bg-tokens-neutral-bg-white text-tokens-neutral-bg-action hover:bg-tokens-neutral-fill-fill-6'
                >
                  <Typography
                    fontFamily='geist'
                    fontSize='bodyMd'
                    fontWeight='medium'
                    className='text-inherit'
                  >
                    Cancel
                  </Typography>
                </Button>
                <Button
                  variant='primary'
                  onClick={() => handleFeedback('negative', selectedReason, otherReason)}
                  isDisabled={!selectedReason || (selectedReason === 'Other' && !otherReason)}
                  className='h-9'
                >
                  <Typography
                    fontFamily='geist'
                    fontSize='bodyMd'
                    fontWeight='medium'
                    className='text-inherit'
                  >
                    Done
                  </Typography>
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
